/**
 * Shared authentication URL building logic.
 *
 * The login link now points at the same-origin `/auth/login` route, which
 * generates PKCE + state, stores them in short-lived cookies, and redirects to
 * the authorization server. Keeping the heavy lifting in a route (rather than
 * in the link) means PKCE state is created at navigation time, not when every
 * login link is rendered. This module stays environment-agnostic (server +
 * client).
 */
export interface BuildLoginUrlParams {
  /** The full current URL to return to after authentication */
  currentUrl: string;
  /** The origin (protocol + host) for building the login URL */
  origin: string;
  /** The locale for the login page */
  locale: string;
}

/**
 * Builds the same-origin `/auth/login` URL, preserving the return-to target and
 * locale. The `/auth/login` route handles PKCE/state and the redirect to the
 * authorization server.
 */
export function buildLoginUrl(params: BuildLoginUrlParams): string {
  const { currentUrl, origin, locale } = params;
  const url = new URL('/auth/login', origin);
  url.searchParams.set('returnTo', currentUrl);
  url.searchParams.set('locale', locale);
  return url.toString();
}
